import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import * as THREE from 'three'
import { AiOutlineArrowLeft, AiOutlineFullscreen, AiOutlineFullscreenExit, AiOutlineReload, AiOutlineDownload } from 'react-icons/ai'
import { useNavigate } from 'react-router-dom'
import GIF from 'gif.js-upgrade'

// Translations
const translations = {
    en: {
        title: 'Moment of Inertia',
        subtitle: 'Drag to rotate view',
        selectShape: 'Select Shape',
        rotationSpeed: 'Rotation Speed',
        aboutTitle: 'About Moment of Inertia',
        aboutText1: "The moment of inertia (I) measures an object's resistance to changes in rotation. It depends on mass distribution and the axis of rotation.",
        aboutText2: 'Rotational kinetic energy is analogous to linear kinetic energy, with I replacing mass (m) and angular velocity (ω) replacing velocity (v).',
        createdBy: 'Created by Greg Mulcair. Inspired by',
        resetView: 'Reset view',
        shapes: [
            { name: 'Point Mass', description: 'Single mass at distance R from axis' },
            { name: 'Thin Rod (end)', description: 'Uniform rod, axis at one end' },
            { name: 'Thin Rod (center)', description: 'Uniform rod, axis through center' },
            { name: 'Ring (perpendicular)', description: 'Thin ring, axis perpendicular to plane' },
            { name: 'Ring (diameter)', description: 'Thin ring, axis along diameter' },
            { name: 'Solid Cylinder', description: 'Uniform cylinder, axis through center' },
            { name: 'Solid Disk', description: 'Thin disk, axis perpendicular to plane' },
            { name: 'Solid Sphere', description: 'Uniform solid sphere' },
            { name: 'Hollow Sphere', description: 'Thin spherical shell' },
            { name: 'Rectangular Plate', description: 'Thin plate, axis through center' },
        ]
    },
    fr: {
        title: "Moment d'inertie",
        subtitle: 'Glisser pour faire pivoter la vue',
        selectShape: 'Sélectionner la forme',
        rotationSpeed: 'Vitesse de rotation',
        aboutTitle: "À propos du moment d'inertie",
        aboutText1: "Le moment d'inertie (I) mesure la résistance d'un objet aux changements de rotation. Il dépend de la distribution de la masse et de l'axe de rotation.",
        aboutText2: "L'énergie cinétique de rotation est analogue à l'énergie cinétique linéaire, avec I remplaçant la masse (m) et la vitesse angulaire (ω) remplaçant la vitesse (v).",
        createdBy: 'Créé par Greg Mulcair. Inspiré par',
        resetView: 'Réinitialiser la vue',
        shapes: [
            { name: 'Masse ponctuelle', description: "Masse unique à distance R de l'axe" },
            { name: 'Tige mince (extrémité)', description: "Tige uniforme, axe à une extrémité" },
            { name: 'Tige mince (centre)', description: 'Tige uniforme, axe au centre' },
            { name: 'Anneau (perpendiculaire)', description: 'Anneau mince, axe perpendiculaire au plan' },
            { name: 'Anneau (diamètre)', description: "Anneau mince, axe le long du diamètre" },
            { name: 'Cylindre plein', description: 'Cylindre uniforme, axe au centre' },
            { name: 'Disque plein', description: 'Disque mince, axe perpendiculaire au plan' },
            { name: 'Sphère pleine', description: 'Sphère solide uniforme' },
            { name: 'Sphère creuse', description: 'Coque sphérique mince' },
            { name: 'Plaque rectangulaire', description: 'Plaque mince, axe au centre' },
        ]
    }
}

// Shape formulas (same in both languages)
const SHAPE_FORMULAS = [
    'I = MR²',
    'I = ⅓ML²',
    'I = ¹⁄₁₂ML²',
    'I = MR²',
    'I = ½MR²',
    'I = ½MR²',
    'I = ½MR²',
    'I = ⅖MR²',
    'I = ⅔MR²',
    'I = ¹⁄₁₂M(a²+b²)',
]

// Rotation axis (vertical red line)
const RotationAxis = () => (
    <mesh>
        <cylinderGeometry args={[0.02, 0.02, 8, 16]} />
        <meshStandardMaterial color="#ff4444" />
    </mesh>
)

// Shape 0: Point Mass - single ball orbiting
const PointMass = ({ speed }) => {
    const groupRef = useRef()

    useFrame((state, delta) => {
        if (groupRef.current) {
            groupRef.current.rotation.y += delta * speed * 0.5
        }
    })

    return (
        <group ref={groupRef}>
            {/* Single mass ball */}
            <mesh position={[1.4, 0, 0]}>
                <sphereGeometry args={[0.15, 24, 24]} />
                <meshStandardMaterial color="#7080a0" metalness={0.3} roughness={0.5} />
            </mesh>
            {/* Orbital path indicator */}
            <mesh rotation={[Math.PI / 2, 0, 0]}>
                <torusGeometry args={[1.4, 0.01, 8, 64]} />
                <meshBasicMaterial color="#4488ff" transparent opacity={0.5} />
            </mesh>
        </group>
    )
}

// Shape 1: Thin Rod (end) - actual rod from axis outward
const ThinRodEnd = ({ speed }) => {
    const groupRef = useRef()

    useFrame((state, delta) => {
        if (groupRef.current) {
            groupRef.current.rotation.y += delta * speed * 0.5
        }
    })

    const rodLength = 1.6

    return (
        <group ref={groupRef}>
            {/* Actual rod - cylinder rotated to be horizontal */}
            <mesh position={[rodLength / 2, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
                <cylinderGeometry args={[0.06, 0.06, rodLength, 16]} />
                <meshStandardMaterial color="#7080a0" metalness={0.3} roughness={0.5} />
            </mesh>
            {/* End cap */}
            <mesh position={[rodLength, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
                <sphereGeometry args={[0.06, 16, 16]} />
                <meshStandardMaterial color="#7080a0" metalness={0.3} roughness={0.5} />
            </mesh>
        </group>
    )
}

// Shape 2: Thin Rod (center) - actual rod through center
const ThinRodCenter = ({ speed }) => {
    const groupRef = useRef()

    useFrame((state, delta) => {
        if (groupRef.current) {
            groupRef.current.rotation.y += delta * speed * 0.5
        }
    })

    const rodLength = 2.8

    return (
        <group ref={groupRef}>
            {/* Actual rod - cylinder rotated to be horizontal */}
            <mesh rotation={[0, 0, Math.PI / 2]}>
                <cylinderGeometry args={[0.06, 0.06, rodLength, 16]} />
                <meshStandardMaterial color="#7080a0" metalness={0.3} roughness={0.5} />
            </mesh>
            {/* End caps */}
            <mesh position={[rodLength / 2, 0, 0]}>
                <sphereGeometry args={[0.06, 16, 16]} />
                <meshStandardMaterial color="#7080a0" metalness={0.3} roughness={0.5} />
            </mesh>
            <mesh position={[-rodLength / 2, 0, 0]}>
                <sphereGeometry args={[0.06, 16, 16]} />
                <meshStandardMaterial color="#7080a0" metalness={0.3} roughness={0.5} />
            </mesh>
        </group>
    )
}

// Shape 3: Ring (perpendicular) - horizontal ring (torus)
const RingPerpendicular = ({ speed }) => {
    const groupRef = useRef()

    useFrame((state, delta) => {
        if (groupRef.current) {
            groupRef.current.rotation.y += delta * speed * 0.5
        }
    })

    return (
        <group ref={groupRef}>
            {/* Solid torus ring */}
            <mesh rotation={[Math.PI / 2, 0, 0]}>
                <torusGeometry args={[1.4, 0.08, 16, 48]} />
                <meshStandardMaterial color="#7080a0" metalness={0.3} roughness={0.5} />
            </mesh>
            {/* Faint wireframe overlay */}
            <mesh rotation={[Math.PI / 2, 0, 0]}>
                <torusGeometry args={[1.4, 0.082, 12, 24]} />
                <meshBasicMaterial color="#a0b0c0" wireframe transparent opacity={0.15} />
            </mesh>
        </group>
    )
}

// Shape 4: Ring (diameter) - vertical ring rotating around Y
const RingDiameter = ({ speed }) => {
    const groupRef = useRef()

    useFrame((state, delta) => {
        if (groupRef.current) {
            groupRef.current.rotation.y += delta * speed * 0.5
        }
    })

    return (
        <group ref={groupRef}>
            {/* Solid torus ring - vertical orientation */}
            <mesh>
                <torusGeometry args={[1.4, 0.08, 16, 48]} />
                <meshStandardMaterial color="#7080a0" metalness={0.3} roughness={0.5} />
            </mesh>
        </group>
    )
}

// Shape 5: Solid Cylinder with surface
const SolidCylinder = ({ speed }) => {
    const groupRef = useRef()

    useFrame((state, delta) => {
        if (groupRef.current) {
            groupRef.current.rotation.y += delta * speed * 0.5
        }
    })

    return (
        <group ref={groupRef}>
            {/* Single solid cylinder with closed caps */}
            <mesh>
                <cylinderGeometry args={[0.8, 0.8, 1.8, 48]} />
                <meshStandardMaterial
                    color="#7080a0"
                    metalness={0.2}
                    roughness={0.6}
                />
            </mesh>
            {/* Faint wireframe overlay - on side surface only */}
            <mesh>
                <cylinderGeometry args={[0.82, 0.82, 1.78, 16, 4, true]} />
                <meshBasicMaterial color="#a0b0c0" wireframe transparent opacity={0.12} depthWrite={false} />
            </mesh>
        </group>
    )
}

// Shape 6: Solid Disk (thin cylinder)
const SolidDisk = ({ speed }) => {
    const groupRef = useRef()

    useFrame((state, delta) => {
        if (groupRef.current) {
            groupRef.current.rotation.y += delta * speed * 0.5
        }
    })

    return (
        <group ref={groupRef}>
            {/* Single solid disk using closed cylinder */}
            <mesh>
                <cylinderGeometry args={[1.3, 1.3, 0.08, 64]} />
                <meshStandardMaterial
                    color="#7080a0"
                    metalness={0.2}
                    roughness={0.6}
                />
            </mesh>
            {/* Faint wireframe overlay - on edge only */}
            <mesh>
                <cylinderGeometry args={[1.32, 1.32, 0.06, 32, 1, true]} />
                <meshBasicMaterial color="#a0b0c0" wireframe transparent opacity={0.12} depthWrite={false} />
            </mesh>
        </group>
    )
}

// Shape 7: Solid Sphere with surface
const SolidSphere = ({ speed }) => {
    const groupRef = useRef()

    useFrame((state, delta) => {
        if (groupRef.current) {
            groupRef.current.rotation.y += delta * speed * 0.5
        }
    })

    return (
        <group ref={groupRef}>
            {/* Sphere surface */}
            <mesh>
                <sphereGeometry args={[1.3, 48, 32]} />
                <meshStandardMaterial
                    color="#7080a0"
                    metalness={0.3}
                    roughness={0.5}
                />
            </mesh>
            {/* Faint wireframe overlay */}
            <mesh>
                <sphereGeometry args={[1.305, 16, 12]} />
                <meshBasicMaterial color="#a0b0c0" wireframe transparent opacity={0.1} />
            </mesh>
        </group>
    )
}

// Shape 8: Hollow Sphere (shell only)
const HollowSphere = ({ speed }) => {
    const groupRef = useRef()

    useFrame((state, delta) => {
        if (groupRef.current) {
            groupRef.current.rotation.y += delta * speed * 0.5
        }
    })

    return (
        <group ref={groupRef}>
            {/* Hollow sphere - transparent with wireframe look */}
            <mesh>
                <sphereGeometry args={[1.3, 32, 24]} />
                <meshStandardMaterial
                    color="#7080a0"
                    metalness={0.2}
                    roughness={0.6}
                    transparent
                    opacity={0.5}
                    side={THREE.DoubleSide}
                />
            </mesh>
            {/* Wireframe overlay to show it's hollow */}
            <mesh>
                <sphereGeometry args={[1.31, 24, 16]} />
                <meshBasicMaterial color="#4060a0" wireframe />
            </mesh>
        </group>
    )
}

// Shape 9: Rectangular Plate
const RectangularPlate = ({ speed }) => {
    const groupRef = useRef()

    useFrame((state, delta) => {
        if (groupRef.current) {
            groupRef.current.rotation.y += delta * speed * 0.5
        }
    })

    return (
        <group ref={groupRef}>
            {/* Plate as a thin box for proper 3D appearance */}
            <mesh>
                <boxGeometry args={[1.8, 0.05, 1.1]} />
                <meshStandardMaterial
                    color="#7080a0"
                    metalness={0.2}
                    roughness={0.6}
                />
            </mesh>
        </group>
    )
}

// Scene component that renders the appropriate shape
const Scene = ({ selectedShape, rotationSpeed, onControlsChange }) => {
    const controlsRef = useRef()

    const renderShape = () => {
        switch (selectedShape) {
            case 0: return <PointMass speed={rotationSpeed} />
            case 1: return <ThinRodEnd speed={rotationSpeed} />
            case 2: return <ThinRodCenter speed={rotationSpeed} />
            case 3: return <RingPerpendicular speed={rotationSpeed} />
            case 4: return <RingDiameter speed={rotationSpeed} />
            case 5: return <SolidCylinder speed={rotationSpeed} />
            case 6: return <SolidDisk speed={rotationSpeed} />
            case 7: return <SolidSphere speed={rotationSpeed} />
            case 8: return <HollowSphere speed={rotationSpeed} />
            case 9: return <RectangularPlate speed={rotationSpeed} />
            default: return null
        }
    }

    return (
        <>
            {/* Lighting */}
            <ambientLight intensity={0.5} />
            <directionalLight position={[5, 5, 5]} intensity={0.8} />
            <directionalLight position={[-3, 2, -5]} intensity={0.3} />

            {/* Rotation axis */}
            <RotationAxis />

            {/* Shape */}
            {renderShape()}

            {/* Camera controls */}
            <OrbitControls
                ref={controlsRef}
                enablePan={false}
                minDistance={3}
                maxDistance={10}
                onChange={() => onControlsChange && onControlsChange(true)}
            />
        </>
    )
}

const MomentOfInertia = () => {
    const navigate = useNavigate()
    const containerRef = useRef(null)
    const [selectedShape, setSelectedShape] = useState(0)
    const [isFullScreen, setIsFullScreen] = useState(false)
    const [rotationSpeed, setRotationSpeed] = useState(1)
    const [isDarkMode, setIsDarkMode] = useState(false)
    const [language, setLanguage] = useState('en')
    const [viewRotated, setViewRotated] = useState(false)
    const controlsResetRef = useRef(null)

    // Get current translations
    const t = translations[language]

    const resetView = () => {
        setViewRotated(false)
        // Reset will happen via key change
    }

    const toggleLanguage = () => {
        setLanguage(lang => lang === 'en' ? 'fr' : 'en')
    }

    // GIF generation state
    const [isRecordingGif, setIsRecordingGif] = useState(false)
    const [gifProgress, setGifProgress] = useState(0)
    const canvasRef = useRef(null)

    const generateGif = useCallback(async () => {
        if (isRecordingGif) return

        const canvas = containerRef.current?.querySelector('canvas')
        if (!canvas) {
            console.error('Canvas not found')
            return
        }

        setIsRecordingGif(true)
        setGifProgress(0)

        // Create a temporary canvas with white background
        const tempCanvas = document.createElement('canvas')
        const size = 400
        tempCanvas.width = size
        tempCanvas.height = size
        const tempCtx = tempCanvas.getContext('2d')

        // Create GIF encoder
        const gif = new GIF({
            workers: 2,
            quality: 10,
            width: size,
            height: size,
            workerScript: '/gif.worker.js'
        })

        // We'll capture frames by temporarily changing the rotation
        const frames = 60
        const originalSpeed = rotationSpeed

        // Create an offscreen renderer for capturing
        const renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true })
        renderer.setSize(size, size)
        renderer.setClearColor(0xffffff, 1)

        const scene = new THREE.Scene()
        scene.background = new THREE.Color(0xffffff)

        // Add lights
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.6)
        scene.add(ambientLight)
        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8)
        directionalLight.position.set(5, 5, 5)
        scene.add(directionalLight)
        const backLight = new THREE.DirectionalLight(0xffffff, 0.3)
        backLight.position.set(-5, -5, -5)
        scene.add(backLight)

        const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 1000)
        camera.position.set(0, 2, 5)
        camera.lookAt(0, 0, 0)

        // Create the shape geometry based on selectedShape
        const shapeMaterial = new THREE.MeshStandardMaterial({
            color: 0x7080a0,
            metalness: 0.2,
            roughness: 0.6
        })

        const group = new THREE.Group()

        // Add rotation axis
        const axisGeometry = new THREE.CylinderGeometry(0.02, 0.02, 8, 16)
        const axisMaterial = new THREE.MeshStandardMaterial({ color: 0xff4444 })
        const axis = new THREE.Mesh(axisGeometry, axisMaterial)
        group.add(axis)

        // Wireframe material for mesh overlays
        const wireframeMaterial = new THREE.MeshBasicMaterial({
            color: 0xa0b0c0,
            wireframe: true,
            transparent: true,
            opacity: 0.12,
            depthWrite: false
        })

        // Add shape based on selection
        let shapeMesh
        let wireframeMesh
        switch (selectedShape) {
            case 0: // Point Mass
                shapeMesh = new THREE.Mesh(new THREE.SphereGeometry(0.15, 24, 24), shapeMaterial)
                shapeMesh.position.set(1.4, 0, 0)
                // Add orbital path
                const orbitalPath = new THREE.Mesh(
                    new THREE.TorusGeometry(1.4, 0.01, 8, 64),
                    new THREE.MeshBasicMaterial({ color: 0x4488ff, transparent: true, opacity: 0.5 })
                )
                orbitalPath.rotation.x = Math.PI / 2
                group.add(orbitalPath)
                break
            case 1: // Thin Rod (end)
                shapeMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 1.6, 16), shapeMaterial)
                shapeMesh.rotation.z = Math.PI / 2
                shapeMesh.position.set(0.8, 0, 0)
                break
            case 2: // Thin Rod (center)
                shapeMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 2.8, 16), shapeMaterial)
                shapeMesh.rotation.z = Math.PI / 2
                break
            case 3: // Ring (perpendicular)
                shapeMesh = new THREE.Mesh(new THREE.TorusGeometry(1.4, 0.08, 16, 48), shapeMaterial)
                shapeMesh.rotation.x = Math.PI / 2
                // Add wireframe overlay
                wireframeMesh = new THREE.Mesh(new THREE.TorusGeometry(1.4, 0.082, 12, 24), wireframeMaterial)
                wireframeMesh.rotation.x = Math.PI / 2
                group.add(wireframeMesh)
                break
            case 4: // Ring (diameter)
                shapeMesh = new THREE.Mesh(new THREE.TorusGeometry(1.4, 0.08, 16, 48), shapeMaterial)
                break
            case 5: // Solid Cylinder
                shapeMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.8, 0.8, 1.8, 48), shapeMaterial)
                // Add wireframe overlay on side only
                wireframeMesh = new THREE.Mesh(
                    new THREE.CylinderGeometry(0.82, 0.82, 1.78, 16, 4, true),
                    wireframeMaterial
                )
                group.add(wireframeMesh)
                break
            case 6: // Solid Disk
                shapeMesh = new THREE.Mesh(new THREE.CylinderGeometry(1.3, 1.3, 0.08, 64), shapeMaterial)
                // Add wireframe overlay on edge only
                wireframeMesh = new THREE.Mesh(
                    new THREE.CylinderGeometry(1.32, 1.32, 0.06, 32, 1, true),
                    wireframeMaterial
                )
                group.add(wireframeMesh)
                break
            case 7: // Solid Sphere
                shapeMesh = new THREE.Mesh(new THREE.SphereGeometry(1.3, 48, 32), shapeMaterial)
                // Add wireframe overlay
                wireframeMesh = new THREE.Mesh(new THREE.SphereGeometry(1.305, 16, 12), wireframeMaterial)
                group.add(wireframeMesh)
                break
            case 8: // Hollow Sphere
                const hollowMat = new THREE.MeshStandardMaterial({
                    color: 0x7080a0,
                    metalness: 0.2,
                    roughness: 0.6,
                    transparent: true,
                    opacity: 0.5,
                    side: THREE.DoubleSide
                })
                shapeMesh = new THREE.Mesh(new THREE.SphereGeometry(1.3, 32, 24), hollowMat)
                // Add wireframe overlay
                const hollowWireframe = new THREE.Mesh(
                    new THREE.SphereGeometry(1.31, 24, 16),
                    new THREE.MeshBasicMaterial({ color: 0x4060a0, wireframe: true })
                )
                group.add(hollowWireframe)
                break
            case 9: // Rectangular Plate
                shapeMesh = new THREE.Mesh(new THREE.BoxGeometry(1.8, 0.05, 1.1), shapeMaterial)
                break
            default:
                shapeMesh = new THREE.Mesh(new THREE.SphereGeometry(0.5, 32, 32), shapeMaterial)
        }

        if (shapeMesh) {
            group.add(shapeMesh)
        }

        scene.add(group)

        // Capture frames
        for (let i = 0; i < frames; i++) {
            group.rotation.y = (i / frames) * Math.PI * 2
            renderer.render(scene, camera)

            // Draw to temp canvas with white background
            tempCtx.fillStyle = '#ffffff'
            tempCtx.fillRect(0, 0, size, size)
            tempCtx.drawImage(renderer.domElement, 0, 0)

            gif.addFrame(tempCtx, { copy: true, delay: 33 })
            setGifProgress(Math.round((i / frames) * 50))
        }

        // Clean up Three.js resources
        renderer.dispose()
        shapeMaterial.dispose()
        axisMaterial.dispose()

        gif.on('progress', (p) => {
            setGifProgress(50 + Math.round(p * 50))
        })

        gif.on('finished', (blob) => {
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            const shapeNames = ['point-mass', 'thin-rod-end', 'thin-rod-center', 'ring-perpendicular', 'ring-diameter', 'solid-cylinder', 'solid-disk', 'solid-sphere', 'hollow-sphere', 'rectangular-plate']
            a.download = `moment-of-inertia-${shapeNames[selectedShape] || 'shape'}.gif`
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            URL.revokeObjectURL(url)
            setIsRecordingGif(false)
            setGifProgress(0)
        })

        gif.render()
    }, [selectedShape, rotationSpeed, isRecordingGif])

    // Listen for theme changes
    useEffect(() => {
        const checkTheme = () => {
            setIsDarkMode(document.documentElement.classList.contains('dark'))
        }

        checkTheme()

        const observer = new MutationObserver(checkTheme)
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })

        return () => observer.disconnect()
    }, [])

    const toggleFullScreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable full-screen mode: ${err.message}`)
            })
        } else {
            document.exitFullscreen()
        }
    }

    useEffect(() => {
        const handleFullScreenChange = () => {
            setIsFullScreen(!!document.fullscreenElement)
        }

        document.addEventListener('fullscreenchange', handleFullScreenChange)
        return () => {
            document.removeEventListener('fullscreenchange', handleFullScreenChange)
        }
    }, [])

    // Key for resetting the canvas/controls
    const [canvasKey, setCanvasKey] = useState(0)

    const handleResetView = () => {
        setCanvasKey(k => k + 1)
        setViewRotated(false)
    }

    return (
        <div
            ref={containerRef}
            className={`flex flex-col lg:flex-row w-full h-screen ${isDarkMode ? 'bg-slate-900' : 'bg-slate-100'}`}
        >
            {/* Canvas area */}
            <div className="flex-1 relative min-h-[50vh] lg:min-h-0">
                <Canvas
                    key={canvasKey}
                    camera={{ position: [0, 2, 5], fov: 50 }}
                    style={{ background: isDarkMode ? 'linear-gradient(180deg, #1a1a2e 0%, #16213e 100%)' : 'linear-gradient(180deg, #f0f4f8 0%, #e2e8f0 100%)' }}
                >
                    <Scene
                        selectedShape={selectedShape}
                        rotationSpeed={rotationSpeed}
                        onControlsChange={setViewRotated}
                    />
                </Canvas>

                {/* Title overlay */}
                <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-center pointer-events-none">
                    <h1 className={`text-xl md:text-2xl font-bold drop-shadow-lg ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                        {t.title}
                    </h1>
                    <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-slate-600'}`}>
                        {t.subtitle}
                    </p>
                </div>

                {/* Back button */}
                <button
                    onClick={() => navigate('/')}
                    className={`absolute top-4 left-4 p-2 rounded-full transition-colors ${isDarkMode
                        ? 'bg-white/10 hover:bg-white/20'
                        : 'bg-black/10 hover:bg-black/20'
                        }`}
                >
                    <AiOutlineArrowLeft className={`text-2xl ${isDarkMode ? 'text-white' : 'text-slate-800'}`} />
                </button>

                {/* Language switcher */}
                <button
                    onClick={toggleLanguage}
                    className={`absolute top-4 right-40 px-3 py-2 rounded-full transition-colors text-sm font-medium ${isDarkMode
                        ? 'bg-white/10 hover:bg-white/20 text-white'
                        : 'bg-black/10 hover:bg-black/20 text-slate-800'
                        }`}
                    title={language === 'en' ? 'Passer au français' : 'Switch to English'}
                >
                    {language === 'en' ? 'FR' : 'EN'}
                </button>

                {/* Download GIF button */}
                <button
                    onClick={generateGif}
                    disabled={isRecordingGif}
                    className={`absolute top-4 right-28 p-2 rounded-full transition-colors ${isDarkMode
                        ? 'bg-white/10 hover:bg-white/20'
                        : 'bg-black/10 hover:bg-black/20'
                        } ${isRecordingGif ? 'opacity-50 cursor-wait' : ''}`}
                    title={isRecordingGif ? `Generating... ${gifProgress}%` : 'Download GIF'}
                >
                    {isRecordingGif ? (
                        <span className={`text-xs font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                            {gifProgress}%
                        </span>
                    ) : (
                        <AiOutlineDownload className={`text-2xl ${isDarkMode ? 'text-white' : 'text-slate-800'}`} />
                    )}
                </button>

                {/* Reset view button */}
                {viewRotated && (
                    <button
                        onClick={handleResetView}
                        className={`absolute top-4 right-16 p-2 rounded-full transition-colors ${isDarkMode
                            ? 'bg-white/10 hover:bg-white/20'
                            : 'bg-black/10 hover:bg-black/20'
                            }`}
                        title={t.resetView}
                    >
                        <AiOutlineReload className={`text-2xl ${isDarkMode ? 'text-white' : 'text-slate-800'}`} />
                    </button>
                )}

                {/* Fullscreen button */}
                <button
                    onClick={toggleFullScreen}
                    className={`absolute top-4 right-4 p-2 rounded-full transition-colors ${isDarkMode
                        ? 'bg-white/10 hover:bg-white/20'
                        : 'bg-black/10 hover:bg-black/20'
                        }`}
                >
                    {isFullScreen ? (
                        <AiOutlineFullscreenExit className={`text-2xl ${isDarkMode ? 'text-white' : 'text-slate-800'}`} />
                    ) : (
                        <AiOutlineFullscreen className={`text-2xl ${isDarkMode ? 'text-white' : 'text-slate-800'}`} />
                    )}
                </button>
            </div>

            {/* Controls panel */}
            <div className={`w-full lg:w-96 backdrop-blur p-6 overflow-y-auto ${isDarkMode
                ? 'bg-slate-800/90'
                : 'bg-white/90 border-l border-slate-200'
                }`}>
                {/* Shape selector */}
                <div className="mb-6">
                    <h2 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                        {t.selectShape}
                    </h2>
                    <div className="grid grid-cols-2 gap-2">
                        {t.shapes.map((shape, index) => (
                            <button
                                key={index}
                                onClick={() => setSelectedShape(index)}
                                className={`p-3 rounded-lg text-left transition-all ${selectedShape === index
                                    ? 'bg-blue-600 text-white ring-2 ring-blue-400'
                                    : isDarkMode
                                        ? 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                                    }`}
                            >
                                <span className="text-sm font-medium block truncate">{shape.name}</span>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Current shape info */}
                <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-slate-700/50' : 'bg-slate-100'}`}>
                    <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                        {t.shapes[selectedShape].name}
                    </h3>
                    <p className="text-blue-500 font-mono text-xl mb-2">
                        {SHAPE_FORMULAS[selectedShape]}
                    </p>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-slate-600'}`}>
                        {t.shapes[selectedShape].description}
                    </p>
                </div>

                {/* Rotation speed control */}
                <div className="mb-6">
                    <label className={`text-sm font-medium block mb-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                        {t.rotationSpeed}: {rotationSpeed.toFixed(1)}x
                    </label>
                    <input
                        type="range"
                        min="0"
                        max="3"
                        step="0.1"
                        value={rotationSpeed}
                        onChange={(e) => setRotationSpeed(Number(e.target.value))}
                        className={`w-full h-2 rounded-lg appearance-none cursor-pointer accent-blue-500 ${isDarkMode ? 'bg-slate-600' : 'bg-slate-300'
                            }`}
                    />
                </div>

                {/* Info section */}
                <div className={`text-sm space-y-2 ${isDarkMode ? 'text-gray-400' : 'text-slate-600'}`}>
                    <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                        {t.aboutTitle}
                    </h4>
                    <p>
                        {t.aboutText1}
                    </p>
                    <p className="text-blue-500 font-mono">
                        E = ½Iω²
                    </p>
                    <p>
                        {t.aboutText2}
                    </p>
                </div>

                {/* Attribution */}
                <div className={`mt-6 pt-4 border-t ${isDarkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-slate-500'}`}>
                        {t.createdBy}{' '}
                        <a
                            href="https://javalab.org/en/moment_of_inertia_2_en/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:underline"
                        >
                            JavaLab
                        </a>
                    </p>
                </div>
            </div>
        </div>
    )
}

export default MomentOfInertia
